import { useAuth } from '@/lib/authStore';

const BASE = import.meta.env.VITE_API_BASE ?? '/api';

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

type RequestOpts = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  token?: string | null;
  signal?: AbortSignal;
  skipAuth?: boolean;
};

export async function api<T = unknown>(path: string, opts: RequestOpts = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';

  const tokenFromStore = opts.skipAuth ? null : (opts.token ?? useAuth.getState().token);
  if (tokenFromStore) headers.Authorization = `Bearer ${tokenFromStore}`;

  const res = await fetch(`${BASE}${path}`, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });

  const text = await res.text();
  const body = text ? safeJson(text) : null;

  if (res.status === 401 && !opts.skipAuth) {
    useAuth.getState().clear();
  }
  if (!res.ok) {
    const msg =
      (typeof body === 'object' && body && 'error' in body && typeof (body as { error: unknown }).error === 'string'
        ? ((body as { error: string }).error)
        : `HTTP ${res.status}`);
    throw new ApiError(res.status, msg, body);
  }
  return body as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
